import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Container,
  MenuItem,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Typography, TextField } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { myDesignService } from "../../services";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import clsx from "clsx";
import FormHelperText from "@mui/material/FormHelperText";
import { useViewport } from "../../helpers";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { green, red } from "@mui/material/colors";

import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";

import CreatableSelect from "react-select/creatable";
import { DefaultEditor } from "react-simple-wysiwyg";
import ReactFlagsSelect from "react-flags-select";
import { Add, Delete } from "@mui/icons-material";
import {
  createCampaign,
  fetchCampaigns,
  updateCampaign,
} from "../../reducers/myDesigns/myDesignsThunks";
import dayjs from "dayjs";
import i18next from "i18next";

const PREFIX = "CreateCampaignForm";

const classes = {
  mainContainer: `${PREFIX}-mainContainer`,
  formStyle: `${PREFIX}-formStyle`,
  marginBottom: `${PREFIX}-marginBottom`,
  marginTop: `${PREFIX}-marginTop`,
  negateMarginTop: `${PREFIX}-negateMarginTop`,
  greenIcon: `${PREFIX}-greenIcon`,
  redIcon: `${PREFIX}-redIcon`,
  maxInputSize: `${PREFIX}-maxInputSize`,
  fullWidthElement: `${PREFIX}-fullWidthElement`,
  radioTypesContainer: `${PREFIX}-radioTypesContainer`,
  smallContainer: `${PREFIX}-smallContainer`,
  imageContainer: `${PREFIX}-imageContainer`,
  campaignImageContainer: `${PREFIX}-campaignImageContainer`,
  datesContainer: `${PREFIX}-datesContainer`,
  selectFlag: `${PREFIX}-selectFlag`,
  paintingAccordionBorder: `${PREFIX}-paintingAccordionBorder`,
  paintingAccordionBorderSelected: `${PREFIX}-paintingAccordionBorderSelected`,
  loadingContainer: `${PREFIX}-loadingContainer`,
  loadingSpinner: `${PREFIX}-loadingSpinner`,
  selectMenuStyle: `${PREFIX}-selectMenuStyle`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.mainContainer}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.formStyle}`]: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.marginBottom}`]: {
    marginBottom: "1rem !important",
  },

  [`& .${classes.marginTop}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.negateMarginTop}`]: {
    marginTop: "-0.5rem",
  },

  [`& .${classes.greenIcon}`]: {
    color: green[500],
  },

  [`& .${classes.redIcon}`]: {
    color: red[500],
  },

  [`& .${classes.maxInputSize}`]: {
    width: "350px",
  },

  [`& .${classes.fullWidthElement}`]: {
    width: "100%",
  },

  [`& .${classes.radioTypesContainer}`]: {
    display: "flex",
    flexDirection: "row",
    marginBottom: "1rem",
    justifyContent: "space-between",
  },

  [`& .${classes.smallContainer}`]: {
    display: "flex",
    flexDirection: "column",
    width: "30%",
  },

  [`& .${classes.imageContainer}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.campaignImageContainer}`]: {
    width: "100%",
    maxWidth: "300px",
    objectFit: "cover",
    margin: "0",
  },

  [`& .${classes.datesContainer}`]: {
    width: "100%",
  },

  [`& .${classes.selectFlag}`]: {
    "& > button": {
      background: theme.palette.primary.main,
      maxWidth: "200px",
      "& > span": {
        color: "#303030",
      },
    },
    "& > ul": {
      background: "#424242",
      maxHeight: "150px",
      maxWidth: "200px",
      "& > div": {
        background: "#424242",
      },
    },
  },

  [`& .${classes.paintingAccordionBorder}`]: {
    border: "1px solid rgba(211, 175, 95, 0.3)",
  },

  [`& .${classes.paintingAccordionBorderSelected}`]: {
    border: "1px solid",
  },

  [`& .${classes.loadingContainer}`]: {
    minWidth: "250px",
    minHeight: "150px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },

  [`& .${classes.loadingSpinner}`]: {
    marginBottom: "1rem",
  },

  [`& .${classes.selectMenuStyle}`]: {
    backgroundColor: "#424242",
  },
}));

function useDebounce(value, delay) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );

  return debouncedValue;
}

const defaultValues = {
  startDate: dayjs(),
  endDate: null,
  vip: false,
  vipUnique: true,
  fan: false,
  fanUnique: true,
  family: false,
  familyUnique: true,
  tags: [],
};

const MINIMUM_DURATION = 20;

const CreateCampaignForm = (props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { width } = useViewport();
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
    getValues,
    setValue,
    control,
    clearErrors,
  } = useForm({
    mode: "onChange",
    defaultValues,
    shouldUnregister: false,
  });
  const [urls, setUrls] = useState({});
  const [tagsInput, setTagsInput] = useState("");
  const saving = useSelector((state) => state.myDesigns.loading);
  const [isIdentifierUsed, setIsIdentifierUsed] = useState(null);
  const [isIdentifierSearching, setIsIdentifierSearching] = useState(false);
  const debouncedSearchUrlIdentifier = useDebounce(watch("uniqueKey"), 1000);
  const [translations, setTranslations] = useState([
    {
      lang: "FR",
      vip: "Description VIP",
      fan: "Description FAN",
      family: "Description Famille",
    },
  ]);
  const [appDescriptions, setAppDescriptions] = useState([
    {
      lang: "FR",
      vip: "Application description VIP",
      fan: "Application description FAN",
      family: "Application description Famille",
    },
  ]);
  const [fetchCampaign, setFetchCampaign] = useState(null);
  const [loader, setLoader] = useState(props.selectedCampaign ? true : false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [campaignVisible, setCampaignVisible] = useState(false);
  const [notified, setNotified] = useState(false);
  const [uniqueState, setUniqueState] = useState({
    vipUnique: false,
    fanUnique: false,
    familyUnique: false,
  });

  useEffect(() => {
    if (loader) {
      myDesignService.getCampaignInfo(props.selectedCampaign.id).then((res) => {
        setTranslations(res.translations);
        setAppDescriptions(res.appDescriptions);
        setFetchCampaign(res);
        setValue(
          "tags",
          res.tags.map((item) => ({ label: item, value: item }))
        );
        setValue("startDate", res.startDate);
        setValue("endDate", res.endDate);
        setValue("landingUrl", res.landingUrl);
        setValue("uniqueKey", res.uniqueKey);
        setValue("vip", res.vip);
        setValue("fan", res.fan);
        setValue("family", res.family);
        setCampaignVisible(res.visible);
        setNotified(res.notified === 0 ? false : true);
        if (res.entity)
          setSelectedEntity({ name: res.entity.name, id: res.entityId });
        let tmp = {};
        if (res.logoUrl) tmp.logo = res.logoUrl;
        if (res.headerUrl) tmp.imageHeader = res.headerUrl;
        if (res.imageFormUrl) tmp.imageForm = res.imageFormUrl;
        setUrls(tmp);
        setLoader(false);
        setUniqueState({
          vipUnique: res.vipUnique,
          fanUnique: res.fanUnique,
          familyUnique: res.familyUnique,
        });
      });
    }
  }, []);

  useEffect(() => {
    if (
      ((fetchCampaign &&
        fetchCampaign.uniqueKey !== debouncedSearchUrlIdentifier) ||
        !fetchCampaign) &&
      debouncedSearchUrlIdentifier &&
      debouncedSearchUrlIdentifier.length > 0 &&
      !/[^0-9a-zA-Z]/.test(debouncedSearchUrlIdentifier)
    ) {
      setIsIdentifierSearching(true);
      myDesignService
        .campaignUniqueKeyExists(debouncedSearchUrlIdentifier)
        .then((results) => {
          setIsIdentifierSearching(false);
          if (!results.exists) clearErrors("uniqueKey");
          setIsIdentifierUsed(results.exists);
        });
    }
  }, [debouncedSearchUrlIdentifier]);

  function handleImageReader(event, id) {
    let tmp = { ...urls };
    tmp[id] = URL.createObjectURL(event.target.files[0]);
    setUrls(tmp);
  }

  function handleAddTrad() {
    if (!translations) {
      setTranslations([
        {
          lang: "FR",
          vip: "Description VIP",
          fan: "Description FAN",
          family: "Description Famille",
        },
      ]);
    } else {
      let tmp = [...translations];
      tmp.push({
        lang: "FR",
        vip: "Description VIP",
        fan: "Description FAN",
        family: "Description Famille",
      });
      setTranslations(tmp);
    }
  }

  function handleAddDesc() {
    if (!appDescriptions) {
      setAppDescriptions([
        {
          lang: "FR",
          vip: "Application description VIP",
          fan: "Application description FAN",
          family: "Application description Famille",
        },
      ]);
    } else {
      let tmp = [...appDescriptions];
      tmp.push({
        lang: "FR",
        vip: "Application description VIP",
        fan: "Application description FAN",
        family: "Application description Famille",
      });
      setAppDescriptions(tmp);
    }
  }

  function onSubmit(data) {
    let tmp = { ...data };
    tmp.translations = translations;
    tmp.appDescriptions = appDescriptions;
    const target = {
      frameId: props.selectedTarget.frameId,
      label: props.selectedTarget.label,
      id: props.selectedTarget.id,
    };
    let tmpType;
    tmp.uniqueKey = getValues("uniqueKey");
    tmp.notified = data.vip === true ? (notified === true ? 1 : 0) : 0;
    tmp.vipUnique = uniqueState.vipUnique;
    tmp.fanUnique = uniqueState.fanUnique;
    tmp.familyUnique = uniqueState.familyUnique;
    tmpType = data.vip === true ? "VIP" : "";
    tmpType += data.fan ? (tmpType.length > 0 ? "/FAN" : "FAN") : "";
    tmpType += data.family ? (tmpType.length > 0 ? "/FAMILY" : "FAMILY") : "";
    delete tmp.vip;
    delete tmp.fan;
    delete tmp.family;
    tmp.type = tmpType;
    const files = {
      imageHeader: tmp.imageHeader,
      imageForm: tmp.imageForm,
      logo: tmp.logo,
    };
    tmp.visible = campaignVisible;
    const tmpTags = tmp.tags.map((tag) => tag.label);
    tmp.tags = tmpTags;
    delete tmp.imageHeader;
    delete tmp.imageForm;
    delete tmp.logo;
    if (selectedEntity) {
      tmp.entity = selectedEntity;
      tmp.entityId = selectedEntity.id;
    }
    if (props.selectedCampaign) {
      tmp = { ...fetchCampaign, ...tmp };
      updateMyCampaign(tmp, target, files, props.selectedCampaign.id);
    } else {
      createMyCampaign(tmp, target, files);
    }
  }

  const updateMyCampaign = async (campaign, target, files, campaignId) => {
    try {
      const thunk = await dispatch(
        updateCampaign({ campaign, target, files, campaignId })
      );
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
      props.setCreateCampaign();
      enqueueSnackbar(t("toasts.updateCampaignSuccess"), {
        variant: "success",
      });
      try {
        const thunk = await dispatch(
          fetchCampaigns({ frameId: target.frameId, targetId: target.id })
        );
        if (thunk.meta.requestStatus === "rejected")
          throw new Error("recovery error");
      } catch (err) {
        enqueueSnackbar(t("toasts.fetchCampaignsError"), { variant: "error" });
      }
    } catch {
      enqueueSnackbar(t("toasts.updateCampaignError"), { variant: "error" });
    }
  };

  const createMyCampaign = async (campaign, target, files) => {
    try {
      const thunk = await dispatch(createCampaign({ campaign, target, files }));
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
      props.setCreateCampaign();
      enqueueSnackbar(t("toasts.createCampaignSuccess"), {
        variant: "success",
      });
      try {
        const thunk = await dispatch(
          fetchCampaigns({ frameId: target.frameId, targetId: target.id })
        );
        if (thunk.meta.requestStatus === "rejected")
          throw new Error("recovery error");
      } catch (err) {
        enqueueSnackbar(t("toasts.fetchCampaignsError"), { variant: "error" });
      }
    } catch {
      enqueueSnackbar(t("toasts.createCampaignError"), { variant: "error" });
    }
  };

  if (loader) {
    return (
      <StyledDiv>
        <Container>
          <div className={classes.loadingContainer}>
            <CircularProgress
              color={"primary"}
              className={classes.loadingSpinner}
            />
            <Typography>{t("loaders.fetching")}</Typography>
          </div>
        </Container>
      </StyledDiv>
    );
  } else {
    return (
      <StyledDiv>
        <Container className={classes.mainContainer}>
          <form onSubmit={handleSubmit(onSubmit)} className={classes.formStyle}>
            <FormControl
              variant="outlined"
              className={width <= 1024 ? classes.fullWidthElement : ""}
            >
              <InputLabel
                htmlFor="uniqueKey"
                style={{
                  color:
                    (isIdentifierUsed || errors.hasOwnProperty("uniqueKey")) &&
                    "#f44336",
                }}
              >
                {t("myDesign.campaigns.campaignForm.uniqueKey")}
              </InputLabel>
              <OutlinedInput
                defaultValue={fetchCampaign?.uniqueKey.toLowerCase()}
                value={
                  getValues("uniqueKey") && getValues("uniqueKey").toLowerCase()
                }
                disabled={
                  fetchCampaign && fetchCampaign.uniqueKey ? true : false
                }
                {...register("uniqueKey", {
                  required: true,
                  validate: (value) =>
                    !/[^-0-9a-zA-Z]/.test(value) === false
                      ? "regexp error"
                      : value.length > 0 && !isIdentifierUsed
                      ? null
                      : "empty url identifier",
                })}
                name="uniqueKey"
                error={isIdentifierUsed || errors.hasOwnProperty("uniqueKey")}
                className={clsx(
                  classes.marginBottom,
                  width > 1024 ? classes.maxInputSize : ""
                )}
                label={t("myDesign.campaigns.campaignForm.uniqueKey")}
                fullWidth
                required
                variant={"outlined"}
                endAdornment={
                  (isIdentifierUsed !== null || isIdentifierSearching) &&
                  !/[^-0-9a-zA-Z]/.test(getValues("uniqueKey")) && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="check if uniqueKey is available"
                        size="large"
                      >
                        {isIdentifierSearching ? (
                          <CircularProgress />
                        ) : isIdentifierUsed ? (
                          <ClearIcon className={classes.redIcon} />
                        ) : (
                          <CheckIcon className={classes.greenIcon} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  )
                }
              />
            </FormControl>
            {isIdentifierUsed && (
              <FormHelperText
                id="uniqueKeyUsed"
                error
                className={clsx(classes.negateMarginTop, classes.marginBottom)}
              >
                {t("myDesign.campaigns.campaignFormErrors.urlIdentiferUsed")}
              </FormHelperText>
            )}
            {!/[^-0-9a-zA-Z]/.test(getValues("uniqueKey")) === false && (
              <FormHelperText
                id="forbiddenCaracteruniqueKey"
                error
                className={clsx(classes.negateMarginTop, classes.marginBottom)}
              >
                {t("myDesign.campaigns.campaignFormErrors.incorrectIdentifier")}
              </FormHelperText>
            )}
            <TextField
              {...register("name", {
                required: true,
                validate: (value) => (value.length > 0 ? null : "empty name"),
              })}
              defaultValue={fetchCampaign?.name}
              name="name"
              label={t("myDesign.campaigns.campaignForm.name")}
              error={errors.hasOwnProperty("name")}
              className={classes.marginBottom}
              fullWidth
              variant={"outlined"}
            />
            <TextField
              name="landingUrl"
              disabled
              value={
                getValues("uniqueKey")
                  ? `${process.env.REACT_APP_FAN_ART_ZONE}/${getValues(
                      "uniqueKey"
                    )}`
                  : ""
              }
              label={t("myDesign.campaigns.campaignForm.campaignPageUrl")}
              className={classes.marginBottom}
              fullWidth
              variant={"outlined"}
            />
            <FormControlLabel
              control={
                <Checkbox
                  color={"primary"}
                  onChange={(e) => setCampaignVisible(e.target.checked)}
                  checked={
                    campaignVisible || campaignVisible === 1 ? true : false
                  }
                />
              }
              label={t("myDesign.campaigns.campaignForm.campaignVisible")}
            />
            <FormControl className={classes.marginBottom}>
              <InputLabel id="select-entity-label">
                {t("myDesign.campaigns.campaignForm.linkedEntity")}
              </InputLabel>
              <Select
                labelId="select-entity-label"
                id="select-entity-label"
                label={t("myDesign.campaigns.campaignForm.linkedEntity")}
                value={selectedEntity ? selectedEntity.id : ""}
                onChange={(e) =>
                  setSelectedEntity(
                    props.entities.find((ent) => ent.id === e.target.value)
                  )
                }
                MenuProps={{ classes: { paper: classes.selectMenuStyle } }}
              >
                {props.entities.map((z) => (
                  <MenuItem key={z.id} value={z.id}>
                    {z.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div className={clsx(classes.marginBottom, classes.datesContainer)}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={i18next.language.substring(0, 2)}
              >
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => {
                    return (
                      <DatePicker
                        sx={{ marginBottom: "1rem" }}
                        value={dayjs(field.value)}
                        onChange={(date) => field.onChange(date)}
                        id="start-date-picker-dialog"
                        minDate={!props.selectedCampaign ? dayjs() : null}
                        maxDate={dayjs(watch("endDate"))}
                        label={t(
                          "myDesign.campaigns.campaignForm.startDateDataCollection"
                        )}
                        style={{ width: "100%" }}
                      />
                    );
                  }}
                />
                <Controller
                  name="endDate"
                  control={control}
                  rules={{
                    validate: (value) => (!value ? "endDateError" : null),
                  }}
                  render={({ field }) => {
                    return (
                      <DatePicker
                        value={dayjs(field.value)}
                        onChange={(date) => field.onChange(date)}
                        id="end-date-picker-dialog"
                        minDate={dayjs(watch("startDate"))}
                        style={{ width: "100%" }}
                        error={errors.hasOwnProperty("endDate")}
                        label={t(
                          "myDesign.campaigns.campaignForm.endDateDataCollection"
                        )}
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </div>
            <Typography variant="h6">
              {t("myDesign.campaigns.campaignForm.campaignType")}
            </Typography>
            <div className={classes.radioTypesContainer}>
              <div
                className={clsx(classes.smallContainer, classes.marginBottom)}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      color={"primary"}
                      defaultChecked={fetchCampaign?.type.includes("VIP")}
                    />
                  }
                  label={t("myDesign.campaigns.campaignForm.vip")}
                  name="vip"
                  {...register("vip", {
                    validate: (value) =>
                      value || watch("fan") || watch("family")
                        ? null
                        : "noCheckbox",
                  })}
                  className={
                    watch("fan") || watch("vip") || watch("family")
                      ? classes.marginBottom
                      : ""
                  }
                />
                {watch("vip") && (
                  <div>
                    <TextField
                      {...register("durationVideoVip", {
                        required: watch("vip") ? true : false,
                        validate: (value) =>
                          /^\d+$/.test(value) ? null : "errorVipDuration",
                      })}
                      name="durationVideoVip"
                      defaultValue={
                        fetchCampaign && fetchCampaign.durationVideoVip
                          ? fetchCampaign.durationVideoVip
                          : MINIMUM_DURATION
                      }
                      error={errors.hasOwnProperty("durationVideoVip")}
                      label={t("myDesign.campaigns.campaignForm.vipDuration")}
                      className={clsx(classes.marginBottom)}
                      fullWidth
                      variant={"outlined"}
                    />
                    {errors.hasOwnProperty("durationVideoVip") && (
                      <FormHelperText
                        id="vipDurationNotInteger"
                        error
                        className={clsx(
                          classes.negateMarginTop,
                          classes.marginBottom
                        )}
                      >
                        {t("myDesign.campaigns.campaignFormErrors.notInteger")}
                      </FormHelperText>
                    )}
                    <TextField
                      {...register("codeVip", {
                        required: true,
                        validate: (value) =>
                          value.length >= 5 ? null : "lengthVipCode",
                      })}
                      name="codeVip"
                      defaultValue={fetchCampaign?.codeVip}
                      error={errors.hasOwnProperty("codeVip")}
                      label={t("myDesign.campaigns.campaignForm.vipCode")}
                      className={clsx(classes.marginBottom)}
                      placeholder={t("myDesign.campaigns.campaignForm.vipCode")}
                      fullWidth
                      variant={"outlined"}
                    />
                    {errors.hasOwnProperty("codeVip") && (
                      <FormHelperText
                        id="lengthVipCode"
                        error
                        className={clsx(
                          classes.negateMarginTop,
                          classes.marginBottom
                        )}
                      >
                        {t(
                          "myDesign.campaigns.campaignFormErrors.securityCode"
                        )}
                      </FormHelperText>
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          color={"primary"}
                          checked={uniqueState.vipUnique}
                          onChange={(e) =>
                            setUniqueState({
                              ...uniqueState,
                              vipUnique: e.target.checked,
                            })
                          }
                        />
                      }
                      label={t("myDesign.campaigns.campaignForm.uniqueStory")}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          color={"primary"}
                          onChange={(e) => setNotified(e.target.checked)}
                          checked={notified}
                        />
                      }
                      label={t(
                        "myDesign.campaigns.campaignForm.campaignNotified"
                      )}
                    />
                  </div>
                )}
              </div>
              <div className={classes.smallContainer}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color={"primary"}
                      defaultChecked={fetchCampaign?.type.includes("FAN")}
                    />
                  }
                  label={t("myDesign.campaigns.campaignForm.fan")}
                  name="fan"
                  {...register("fan", {
                    validate: (value) =>
                      value || watch("vip") || watch("family")
                        ? null
                        : "noCheckbox",
                  })}
                  className={
                    watch("fan") || watch("vip") || watch("family")
                      ? classes.marginBottom
                      : ""
                  }
                />
                {watch("fan") && (
                  <div>
                    <TextField
                      {...register("durationVideoFan", {
                        required: watch("fan") ? true : false,
                        validate: (value) =>
                          /^\d+$/.test(value) ? null : "errorFanDuration",
                      })}
                      name="durationVideoFan"
                      error={errors.hasOwnProperty("durationVideoFan")}
                      label={t("myDesign.campaigns.campaignForm.fanDuration")}
                      className={clsx(classes.marginBottom)}
                      defaultValue={
                        fetchCampaign && fetchCampaign.durationVideoFan
                          ? fetchCampaign.durationVideoFan
                          : MINIMUM_DURATION
                      }
                      fullWidth
                      variant={"outlined"}
                    />
                    {errors.hasOwnProperty("durationVideoFan") && (
                      <FormHelperText
                        id="fanDurationNotInteger"
                        error
                        className={clsx(
                          classes.negateMarginTop,
                          classes.marginBottom
                        )}
                      >
                        {t("myDesign.campaigns.campaignFormErrors.notInteger")}
                      </FormHelperText>
                    )}
                    <TextField
                      {...register("codeFan")}
                      name="codeFan"
                      error={errors.hasOwnProperty("codeFan")}
                      defaultValue={fetchCampaign?.codeFan}
                      label={t("myDesign.campaigns.campaignForm.fanCode")}
                      className={clsx(classes.marginBottom)}
                      placeholder={t("myDesign.campaigns.campaignForm.fanCode")}
                      fullWidth
                      variant={"outlined"}
                    />
                    {errors.hasOwnProperty("codeFan") && (
                      <FormHelperText
                        id="lengthFanCode"
                        error
                        className={clsx(
                          classes.negateMarginTop,
                          classes.marginBottom
                        )}
                      >
                        {t(
                          "myDesign.campaigns.campaignFormErrors.securityCode"
                        )}
                      </FormHelperText>
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          color={"primary"}
                          checked={uniqueState.fanUnique}
                          onChange={(e) =>
                            setUniqueState({
                              ...uniqueState,
                              fanUnique: e.target.checked,
                            })
                          }
                        />
                      }
                      label={t("myDesign.campaigns.campaignForm.uniqueStory")}
                    />
                  </div>
                )}
              </div>
              <div className={classes.smallContainer}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color={"primary"}
                      defaultChecked={fetchCampaign?.type.includes("FAMILY")}
                    />
                  }
                  label={t("myDesign.campaigns.campaignForm.family")}
                  name="family"
                  {...register("family", {
                    validate: (value) =>
                      value || watch("fan") || watch("vip")
                        ? null
                        : "noCheckbox",
                  })}
                  className={
                    watch("fan") || watch("vip") || watch("family")
                      ? classes.marginBottom
                      : ""
                  }
                />
                {watch("family") && (
                  <div>
                    <TextField
                      {...register("durationVideoFamily", {
                        required: watch("family") ? true : false,
                        validate: (value) =>
                          /^\d+$/.test(value) ? null : "errorFamilyDuration",
                      })}
                      name="durationVideoFamily"
                      error={errors.hasOwnProperty("durationVideoFamily")}
                      label={t(
                        "myDesign.campaigns.campaignForm.familyDuration"
                      )}
                      className={clsx(classes.marginBottom)}
                      defaultValue={
                        fetchCampaign && fetchCampaign.durationVideoFamily
                          ? fetchCampaign.durationVideoFamily
                          : MINIMUM_DURATION
                      }
                      fullWidth
                      variant={"outlined"}
                    />
                    {errors.hasOwnProperty("durationVideoFamily") && (
                      <FormHelperText
                        id="familyDurationNotInteger"
                        error
                        className={clsx(
                          classes.negateMarginTop,
                          classes.marginBottom
                        )}
                      >
                        {t("myDesign.campaigns.campaignFormErrors.notInteger")}
                      </FormHelperText>
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          color={"primary"}
                          checked={uniqueState.familyUnique}
                          onChange={(e) =>
                            setUniqueState({
                              ...uniqueState,
                              familyUnique: e.target.checked,
                            })
                          }
                        />
                      }
                      label={t("myDesign.campaigns.campaignForm.uniqueStory")}
                    />
                  </div>
                )}
              </div>
            </div>
            {errors.hasOwnProperty("vip") &&
              errors.hasOwnProperty("family") &&
              errors.hasOwnProperty("fan") && (
                <FormHelperText
                  id="noCheckbox"
                  error
                  className={clsx(
                    classes.negateMarginTop,
                    classes.marginBottom
                  )}
                >
                  {t(
                    "myDesign.campaigns.campaignFormErrors.selectCampaignType"
                  )}
                </FormHelperText>
              )}
            <TextField
              name="cost"
              defaultValue={fetchCampaign?.cost}
              {...register("cost")}
              placeholder={t(
                "myDesign.campaigns.campaignForm.amountPlaceholder"
              )}
              label={t("myDesign.campaigns.campaignForm.paidCampaign")}
              className={clsx(classes.marginBottom)}
              fullWidth
              variant={"outlined"}
            />
            <div className={classes.imageContainer}>
              <Typography variant="h6">
                {t("myDesign.campaigns.campaignForm.campaignLogo")}
              </Typography>
              <div className={classes.uploadField}>
                <div className={classes.paintingContainer}>
                  <img
                    id="logo"
                    className={classes.campaignImageContainer}
                    alt={watch("logo") ? "campaign logo" : null}
                    src={urls.hasOwnProperty("logo") ? urls["logo"] : undefined}
                  />
                  <div>
                    <Button
                      variant="contained"
                      color="primary"
                      component="label"
                      className={classes.uploadButton}
                      onChange={(e) => handleImageReader(e, "logo")}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {t("myDesign.zones.uploadVideo.selectFileButton")}
                      <input
                        type="file"
                        accept="image/*"
                        name="logo"
                        {...register("logo")}
                        hidden
                      />
                    </Button>
                    <Typography variant="body1">
                      {watch("logo") && watch("logo").length > 0
                        ? watch("logo")[0].name
                        : ""}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.imageContainer}>
              <Typography variant="h6">
                {t("myDesign.campaigns.campaignForm.presentationCampaignImage")}
              </Typography>
              <div className={classes.uploadField}>
                <div className={classes.paintingContainer}>
                  <img
                    id="presentationCampaignImage"
                    className={classes.campaignImageContainer}
                    alt={
                      watch("imageHeader")
                        ? "presentation Campaign Image"
                        : null
                    }
                    src={
                      urls.hasOwnProperty("imageHeader")
                        ? urls["imageHeader"]
                        : undefined
                    }
                  />
                  <div>
                    <Button
                      variant="contained"
                      color="primary"
                      component="label"
                      className={classes.uploadButton}
                      onChange={(e) => handleImageReader(e, "imageHeader")}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {t("myDesign.zones.uploadVideo.selectFileButton")}
                      <input
                        type="file"
                        accept="image/*"
                        name="imageHeader"
                        {...register("imageHeader", {
                          required:
                            (fetchCampaign && !fetchCampaign.headerUrl) ||
                            (!fetchCampaign && !watch("imageHeader")) ||
                            (!fetchCampaign &&
                              watch("imageHeader").length === 0)
                              ? true
                              : false,
                        })}
                        hidden
                      />
                    </Button>
                    <Typography variant="body1">
                      {watch("imageHeader") && watch("imageHeader").length > 0
                        ? watch("imageHeader")[0].name
                        : ""}
                    </Typography>
                  </div>
                </div>
              </div>
              {errors.hasOwnProperty("imageHeader") && (
                <FormHelperText
                  id="noImageHeader"
                  error
                  className={clsx(classes.marginBottom)}
                >
                  {t("myDesign.campaigns.campaignFormErrors.noImageHeader")}
                </FormHelperText>
              )}
            </div>
            <div className={classes.imageContainer}>
              <Typography variant="h6">
                {t("myDesign.campaigns.campaignForm.presentationFormImage")}
              </Typography>
              <div className={classes.uploadField}>
                <div className={classes.paintingContainer}>
                  <img
                    id="presentationFormImage"
                    className={classes.campaignImageContainer}
                    alt={watch("imageForm") ? "presentation Form Image" : null}
                    src={
                      urls.hasOwnProperty("imageForm")
                        ? urls["imageForm"]
                        : undefined
                    }
                  />
                  <div>
                    <Button
                      variant="contained"
                      color="primary"
                      component="label"
                      className={classes.uploadButton}
                      onChange={(e) => handleImageReader(e, "imageForm")}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {t("myDesign.zones.uploadVideo.selectFileButton")}
                      <input
                        type="file"
                        accept="image/*"
                        name="imageForm"
                        {...register("imageForm", {
                          required:
                            (fetchCampaign && !fetchCampaign.imageFormUrl) ||
                            (!fetchCampaign && !watch("imageForm")) ||
                            (!fetchCampaign && watch("imageForm").length === 0)
                              ? true
                              : false,
                        })}
                        hidden
                      />
                    </Button>
                    <Typography variant="body1">
                      {watch("imageForm") && watch("imageForm").length > 0
                        ? watch("imageForm")[0].name
                        : ""}
                    </Typography>
                  </div>
                </div>
              </div>
              {errors.hasOwnProperty("imageForm") && (
                <FormHelperText
                  id="noImageForm"
                  error
                  className={clsx(classes.marginBottom)}
                >
                  {t("myDesign.campaigns.campaignFormErrors.noImageForm")}
                </FormHelperText>
              )}
            </div>
            <div className={clsx(classes.marginTop, classes.marginBottom)}>
              <Typography variant="h6">
                {t("myDesign.campaigns.campaignForm.tagsTitle")}
              </Typography>
              <Controller
                control={control}
                name="tags"
                render={({ onChange }) => (
                  <CreatableSelect
                    isClearable
                    defaultValue={
                      fetchCampaign &&
                      fetchCampaign.tags.map((item) => ({
                        value: item,
                        label: item,
                      }))
                    }
                    isMulti
                    formatCreateLabel={(inputValue) =>
                      `${t(
                        "myDesign.campaigns.campaignForm.addTag"
                      )} ${inputValue}`
                    }
                    noOptionsMessage={() => t("account.form.noResults")}
                    getOptionLabel={(option) =>
                      option.label ? option.label : option.name
                    }
                    placeholder={t(
                      "myDesign.campaigns.campaignForm.addTagPlaceholder"
                    )}
                    onKeyDown={(e) => {
                      if (!tagsInput) return;
                      switch (e.key) {
                        case "Enter":
                          onChange(tagsInput);
                          setTagsInput("");
                          break;
                        case "Tab":
                          onChange(tagsInput);
                          setTagsInput("");
                          break;
                        default:
                          onChange(tagsInput);
                          setTagsInput("");
                      }
                    }}
                    onInputChange={(value) => setTagsInput(value)}
                    onChange={(newValue, actionMeta) => {
                      if (
                        actionMeta.action !== "pop-value" &&
                        actionMeta.action !== "remove-value" &&
                        actionMeta.action !== "clear"
                      ) {
                        const tmpValue = [...newValue];
                        tmpValue.forEach((item) => {
                          item.label = item.value;
                          item.name = item.value;
                        });
                        onChange(tmpValue);
                      } else {
                        onChange(newValue);
                      }
                    }}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                )}
              />
            </div>
            {(watch("vip") ||
              fetchCampaign?.type.includes("VIP") ||
              watch("fan") ||
              fetchCampaign?.type.includes("FAN") ||
              watch("family") ||
              fetchCampaign?.type.includes("FAMILY")) && (
              <Accordion
                expanded={expanded === "translations"}
                onChange={() =>
                  setExpanded(
                    expanded === "translations" ? null : "translations"
                  )
                }
                className={
                  expanded === "translations"
                    ? classes.paintingAccordionBorderSelected
                    : classes.paintingAccordionBorder
                }
                style={{ marginBottom: "1rem" }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                  className={
                    expanded === "translations"
                      ? classes.paintingAccordionBorderSelected
                      : ""
                  }
                  style={{ backgroundColor: "#424242" }}
                >
                  <Typography variant="h6">
                    {t("myDesign.campaigns.campaignForm.tradTitle")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  style={{
                    paddingRight: "8px",
                    paddingLeft: "8px",
                    backgroundColor: "#424242",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      marginTop: "1rem",
                    }}
                  >
                    <div style={{ display: "flex" }}>
                      <Typography variant="h6">
                        {t("myDesign.campaigns.campaignForm.tradTitleAdd")}
                      </Typography>
                      <IconButton
                        size="small"
                        variant="gold"
                        type="button"
                        onClick={() => handleAddTrad()}
                        title={t(
                          "myDesign.campaigns.campaignForm.addTraductionIcon"
                        )}
                        style={{ backgroundColor: "#424242", color: "#B59A4F" }}
                      >
                        <Add />
                      </IconButton>
                    </div>
                    {translations &&
                      translations.map((t, index) => (
                        <div
                          key={index + 1}
                          style={{
                            position: "relative",
                            paddingBottom: "0.5rem",
                            marginTop: "1rem",
                            border: "1px solid #424242",
                            borderRadius: "1rem",
                          }}
                        >
                          <ReactFlagsSelect
                            className={classes.selectFlag}
                            selected={t.lang}
                            defaultCountry={t.lang}
                            onSelect={(e) => {
                              let tmp = [...translations];
                              tmp[index].lang = e;
                              setTranslations(tmp);
                            }}
                            searchable={true}
                          />
                          {(watch("fan") ||
                            fetchCampaign?.type.includes("FAN")) && (
                            <DefaultEditor
                              value={t.fan}
                              onChange={(e) => {
                                let tmp = [...translations];
                                tmp[index].fan = e.target.value;
                                setTranslations(tmp);
                              }}
                            />
                          )}
                          {(watch("vip") ||
                            fetchCampaign?.type.includes("VIP")) && (
                            <DefaultEditor
                              value={t.vip}
                              onChange={(e) => {
                                let tmp = [...translations];
                                tmp[index].vip = e.target.value;
                                setTranslations(tmp);
                              }}
                            />
                          )}
                          {(watch("family") ||
                            fetchCampaign?.type.includes("FAMILY")) && (
                            <DefaultEditor
                              value={t.family}
                              onChange={(e) => {
                                let tmp = [...translations];
                                tmp[index].family = e.target.value;
                                setTranslations(tmp);
                              }}
                            />
                          )}
                          <IconButton
                            type="button"
                            variant="gold"
                            style={{
                              position: "absolute",
                              right: "0",
                              top: "0",
                              color: "#d3af5f",
                            }}
                            onClick={() => {
                              let tmp = [...translations];
                              tmp.splice(index, 1);
                              setTranslations(tmp);
                            }}
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </div>
                      ))}
                  </div>
                </AccordionDetails>
              </Accordion>
            )}
            {(watch("vip") ||
              fetchCampaign?.type.includes("VIP") ||
              watch("fan") ||
              fetchCampaign?.type.includes("FAN") ||
              watch("family") ||
              fetchCampaign?.type.includes("FAMILY")) && (
              <Accordion
                expanded={expanded === "appDescriptions"}
                onChange={() =>
                  setExpanded(
                    expanded === "appDescriptions" ? null : "appDescriptions"
                  )
                }
                className={
                  expanded === "appDescriptions"
                    ? classes.paintingAccordionBorderSelected
                    : classes.paintingAccordionBorder
                }
                style={{ marginBottom: "1rem" }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                  className={
                    expanded === "appDescriptions"
                      ? classes.paintingAccordionBorderSelected
                      : ""
                  }
                  style={{ backgroundColor: "#424242" }}
                >
                  <Typography variant="h6">
                    {t("myDesign.campaigns.campaignForm.appDescription")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  style={{
                    paddingRight: "8px",
                    paddingLeft: "8px",
                    backgroundColor: "#424242",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      marginTop: "1rem",
                    }}
                  >
                    <div style={{ display: "flex" }}>
                      <Typography variant="h6">
                        {t("myDesign.campaigns.campaignForm.appDescriptionAdd")}
                      </Typography>
                      <IconButton
                        size="small"
                        variant="gold"
                        type="button"
                        onClick={() => handleAddDesc()}
                        title={t("myDesign.campaigns.campaignForm.addDescIcon")}
                        style={{ backgroundColor: "#424242", color: "#B59A4F" }}
                      >
                        <Add />
                      </IconButton>
                    </div>
                    {appDescriptions &&
                      appDescriptions.map((t, index) => (
                        <div
                          key={index + 1}
                          style={{
                            position: "relative",
                            paddingBottom: "0.5rem",
                            marginTop: "1rem",
                            border: "1px solid #424242",
                            borderRadius: "1rem",
                          }}
                        >
                          <ReactFlagsSelect
                            className={classes.selectFlag}
                            selected={t.lang}
                            defaultCountry={t.lang}
                            onSelect={(e) => {
                              let tmp = [...appDescriptions];
                              tmp[index].lang = e;
                              setAppDescriptions(tmp);
                            }}
                            searchable={true}
                          />
                          {(watch("fan") ||
                            fetchCampaign?.type.includes("FAN")) && (
                            <DefaultEditor
                              value={t.fan}
                              onChange={(e) => {
                                let tmp = [...appDescriptions];
                                tmp[index].fan = e.target.value;
                                setAppDescriptions(tmp);
                              }}
                            />
                          )}
                          {(watch("vip") ||
                            fetchCampaign?.type.includes("VIP")) && (
                            <DefaultEditor
                              value={t.vip}
                              onChange={(e) => {
                                let tmp = [...appDescriptions];
                                tmp[index].vip = e.target.value;
                                setAppDescriptions(tmp);
                              }}
                            />
                          )}
                          {(watch("family") ||
                            fetchCampaign?.type.includes("FAMILY")) && (
                            <DefaultEditor
                              value={t.family}
                              onChange={(e) => {
                                let tmp = [...appDescriptions];
                                tmp[index].family = e.target.value;
                                setAppDescriptions(tmp);
                              }}
                            />
                          )}
                          <IconButton
                            type="button"
                            variant="gold"
                            style={{
                              position: "absolute",
                              right: "0",
                              top: "0",
                              color: "#d3af5f",
                            }}
                            onClick={() => {
                              let tmp = [...appDescriptions];
                              tmp.splice(index, 1);
                              setAppDescriptions(tmp);
                            }}
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </div>
                      ))}
                  </div>
                </AccordionDetails>
              </Accordion>
            )}
            <Button
              color="primary"
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              className={clsx(
                classes.marginLeft,
                classes.marginBottom,
                classes.marginTop
              )}
              disabled={saving}
            >
              {saving ? (
                <CircularProgress size={28} color="inherit" />
              ) : props.selectedCampaign ? (
                t("myDesign.campaigns.campaignForm.update")
              ) : (
                t("myDesign.campaigns.campaignForm.create")
              )}
            </Button>
          </form>
        </Container>
      </StyledDiv>
    );
  }
};

export default CreateCampaignForm;
